import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { aalOf, authClient, hasVerifiedFactor, type AuthClient, type Session } from './client.ts'

/** 'signed_out' -> password -> 'mfa_required' (only if the login has a verified TOTP factor) -> 'ready' (M22, docs/STACK.md 9.1: the app's own auth client).
 *  The phase comes from the STORED session at once (an expired token and no network included: the field works offline), then, online, from the server's
 *  answer about the login's factors (`user()`). Making a factor mandatory for privileged roles is the server's (M4, ARCHITECTURE 6.1). */
export type AuthPhase = 'loading' | 'signed_out' | 'mfa_required' | 'ready'
interface AuthApi {
  phase: AuthPhase; session: Session | null; userId: string | null; email: string | null
  /** `error` is the server's code (`invalid_credentials`, `rate_limited`, …) or `network` / `timeout` / `no_public_origin`; `status` 0 when nothing answered */
  signIn(email: string, password: string): Promise<{ error?: string; status?: number }>
  verifyMfa(code: string): Promise<{ error?: string }>
  signOut(): Promise<void>
  refreshPhase(): Promise<void>
}
const Ctx = createContext<AuthApi | null>(null)

/** no session: signed out; a verified factor and a token below aal2: the code first; else ready */
export function phaseOf(s: Session | null): AuthPhase {
  if (!s) return 'signed_out'
  return hasVerifiedFactor(s.user) && aalOf(s) !== 'aal2' ? 'mfa_required' : 'ready'
}

export function AuthProvider({ children, client = authClient }: { children: ReactNode; client?: AuthClient }) {
  const [session, setSession] = useState<Session | null>(() => client.getSession())

  useEffect(() => {
    const off = client.onChange(setSession)
    const stop = client.startAutoRefresh()
    setSession(client.getSession())
    if (client.getSession()) void client.user().catch(() => null)     // online: the login's factors as the server has them (the phase follows)
    return () => { off(); stop() }
  }, [client])

  const phase = phaseOf(session)
  const api = useMemo<AuthApi>(() => ({
    phase, session, userId: session?.user.id ?? null, email: session?.user.email ?? null,
    signIn: (email, password) => client.signIn(email, password),
    async verifyMfa(code) {
      let factor = client.getSession()?.user.factors.find((f) => f.status === 'verified')
      if (!factor) factor = (await client.user())?.factors.find((f) => f.status === 'verified')
      if (!factor) return { error: 'no_factor' }
      const r = await client.verifyFactor(factor.id, code)
      return r.error ? { error: r.error } : {}
    },
    async signOut() { await client.signOut() },
    async refreshPhase() { await client.user() },
  }), [phase, session, client])

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>
}

export function useAuth(): AuthApi {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth outside AuthProvider')
  return v
}
